import logErrors from '@nykaa/logger/logErrors';
import { logger } from '@nykaa/logger';
import { getWidgets } from '@nykaa/app/store/service';
import { statusMessages } from 'shared/constants/statusCodes';
import { USER_DATA_STORAGE_KEY, PRO_COOKIE_KEY } from 'constants/cookies';
import { FETCH_ERROR } from 'shared/components/Error/constant';
import { setCookie } from 'shared/helpers/utils/cookie';
import { ApiHelper } from 'shared/api';
import { ROUTES, getUrl, getGatewayUrl, urls } from 'shared/api/urls';
import { contentTypes } from 'constants/requestHeaders';
import { transformAuthData, transformUserDetails } from './transformer';
import {
  getAuthFromStorage,
  storeAuthInStorage,
  removeAuthFromStorage,
} from './helper';
import { pushEvent } from '@nykaa/data-layer';
import { events } from 'constants/dataLayer';
import { queryStringFromParams } from 'helpers/utils/url';
import { UNAUTHORIZE_ERROR_CODE } from 'shared/constants';
import { deleteCartQtyLocalStorage } from 'helpers/listingPage';

const ERROR_TRANSFORMING_REACT_CONFIG =
  'Error Transforming react config from storage';
const ERROR_FETCHING_PRIVE_INFO = 'Error fetching prive info';

export const getPriveTier = async () => {
  const url = `${getGatewayUrl()}${urls[ROUTES.LOYALTY_INFO]}`;
  const queryParams = {
    source: 'react',
  };
  const queryString = queryStringFromParams(queryParams);
  try {
    const { data } = await ApiHelper(`${url}${queryString}`, 'get');

    if (!data) {
      throw new Error('customer tier details not found');
    }
    const tierName = data.tier_name || 'non_prive';
    return tierName;
  } catch (e) {
    logger.error(e, ERROR_FETCHING_PRIVE_INFO);
    throw e;
  }
};

export const getAuth = async () => {
  const authDataInStorage = getAuthFromStorage();
  const sessionSiteVisited = sessionStorage.getItem('siteVisited');
  if (authDataInStorage && sessionSiteVisited) {
    const { data: { result } = { result: null } } = authDataInStorage;
    if (result) {
      try {
        const transformedReactConfig = transformAuthData(result);

        return Promise.resolve({
          fetchedOverNetwork: false,
          data: transformedReactConfig,
        });
      } catch (err) {
        logErrors(err, ERROR_TRANSFORMING_REACT_CONFIG);
      }
    }
  }
  // The below code is executed if extracting data from storage is not successful
  const url = getUrl(ROUTES.AUTH);
  try {
    const response = await ApiHelper(`${url}`, 'post');
    const { data, status, statusText } = response;

    storeAuthInStorage({
      data: data,
      status: status,
      statusText: statusText,
    });
    sessionStorage.setItem('siteVisited', 'true');
    const {
      data: { result },
    } = response;
    if (!result) {
      return Promise.reject({
        message: '"response" not found in api -> data',
      });
    }
    const transformedAuthData = transformAuthData(result);
    const { groupId, id, isPro } = transformedAuthData;
    // TODO: set auth data in session
    setCookie(
      USER_DATA_STORAGE_KEY,
      JSON.stringify({
        id,
        nykaa_pro: isPro,
        group_id: groupId,
      }),
      1
    );
    setCookie(PRO_COOKIE_KEY, isPro ? true : false, 1);

    return Promise.resolve({
      fetchedOverNetwork: true,
      data: transformedAuthData,
    });
  } catch (err) {
    // ? for axios: http status < 200 || status >= 300 => error
    // * https://github.com/axios/axios/issues/472#issuecomment-252584718
    const customMessage = 'Error in react config api';
    logErrors(err, customMessage);
    throw err;
  }
};

export const logOut = async () => {
  const url = getUrl(ROUTES.LOGOUT);

  try {
    const { data } = await ApiHelper(`${url}`, 'get', undefined, {
      'Content-Type': contentTypes.URL_ENCODED,
    });
    // ! When user logs out we need to remove config from storage
    removeAuthFromStorage();
    pushEvent(events.USER_LOGOUT, {
      logout_type: 'logout',
    });
    deleteCartQtyLocalStorage();

    // update in localStorage
    if (!data) {
      return Promise.reject({
        message: '"data" not found in api',
      });
    }

    return true;
  } catch (err) {
    const customMessage = 'Error in logout api';
    logErrors(err, customMessage);
    // ? for axios: http status < 200 || status >= 300 => error
    // * https://github.com/axios/axios/issues/472#issuecomment-252584718
    throw err;
  }
};

interface LoginParams {
  id: string;
  platform: string;
  password: string;
  smart: string;
}

export const doLogin = async (params: LoginParams) => {
  // TODO: need to remove React Storage.
  const { id, platform, password, smart } = params;
  const url = getUrl(ROUTES.LOGIN);
  const emailCheck = params.id.search('@');
  const formData = new FormData();
  if (emailCheck > 0) {
    formData.append('login[username]', id);
  } else {
    formData.append('login[mobile_number]', id);
  }
  formData.append('login[password]', password);
  formData.append('login[smart]', smart);
  formData.append('login[platform]', platform);

  try {
    removeAuthFromStorage();
    const { data } = await ApiHelper(`${url}`, 'post', formData, {
      'Content-Type': contentTypes.URL_ENCODED,
    });
    if (!data) {
      return Promise.reject('Login data not found');
    }

    return data;
  } catch (e) {
    const customMessage = 'Error Fetching Login API';
    logErrors(e, customMessage);
    throw e;
  }
};

export const fetchMinimalWishlist = async (): Promise<string[]> => {
  const url = getUrl(ROUTES.WISHLIST_IDS);
  const formData = new FormData();
  formData.append('source', 'react');

  try {
    const apiResponse = await ApiHelper(`${url}`, 'post', formData);
    const { data } = apiResponse;

    const { status, response, error: errorCode, message } = data || {};

    if (!(status === statusMessages.SUCCESS) || !response) {
      throw new Error(`${errorCode}: WishlistIds - ${message}`);
    }
    const { wishlist_products: wishlistProducts } = response;
    return wishlistProducts;
  } catch (err) {
    const customMessage = 'Error in wishlist ids api';
    logErrors(err, customMessage);
  }
};

export const logOutAll = async () => {
  const url = getUrl(ROUTES.LOGOUT_ALL);
  const formData = new FormData();
  formData.append('source', 'react');
  try {
    const { data } = await ApiHelper(`${url}`, 'post', formData);
    // ! When user logs out we need to remove config from storage
    removeAuthFromStorage();
    pushEvent(events.USER_LOGOUT, {
      logout_type: 'logoutAll',
    });
    deleteCartQtyLocalStorage();

    // update in localStorage
    if (!data) {
      return Promise.reject({
        message: '"data" not found in api',
      });
    }

    return true;
  } catch (err) {
    const customMessage = 'Error in logout all session api';
    logErrors(err, customMessage);
    // ? for axios: http status < 200 || status >= 300 => error
    // * https://github.com/axios/axios/issues/472#issuecomment-252584718
    throw err;
  }
};

export const getUserHash = async () => {
  // The below code is executed if extracting data from storage is not successful
  const url = getUrl(ROUTES.CHAT_API);
  try {
    const params = {
      mode: 'popout',
      deviceType: 'mobile',
    };
    const { data: response } = await ApiHelper(`${url}`, 'post', params);
    const { data: { hash = '', unqTxnId = '', phoneNo = '' } = {} } =
      response || {};
    return { hash, unqTxnId, phoneNo };
  } catch (err) {
    // ? for axios: http status < 200 || status >= 300 => error
    // * https://github.com/axios/axios/issues/472#issuecomment-252584718
    const customMessage = 'Error in getting Verloop hash';
    logErrors(err, customMessage);
  }
};

export const getUserDetails = async () => {
  const url = getUrl(ROUTES.USER_DETAILS);
  const urlParams = new URLSearchParams();
  urlParams.append('source', 'react');
  try {
    const {
      data: { response },
    } = await ApiHelper(`${url}`, 'POST', urlParams, {
      'Content-Type': contentTypes.URL_ENCODED,
    });

    if (!response) {
      throw new Error('"result" not found user details api');
    }
    return transformUserDetails(response);
  } catch (e) {
    logger.error(e, 'Error fetching User Details');
    throw e;
  }
};

export const getReactConfig = async () => {
  const url = getUrl(ROUTES.AUTH);
  try {
    const response = await ApiHelper(`${url}`, 'post');
    const { data, status, statusText } = response;

    storeAuthInStorage({
      data: data,
      status: status,
      statusText: statusText,
    });
    sessionStorage.setItem('siteVisited', 'true');
    const {
      data: { result },
    } = response;
    if (!result) {
      return Promise.reject({
        message: `${UNAUTHORIZE_ERROR_CODE}: "response" not found in api -> data`,
      });
    }
    const transformedAuthData = transformAuthData(result);
    const { groupId, id, isPro } = transformedAuthData;
    // TODO: set auth data in session
    setCookie(
      USER_DATA_STORAGE_KEY,
      JSON.stringify({
        id,
        nykaa_pro: isPro,
        group_id: groupId,
      }),
      1
    );
    setCookie(PRO_COOKIE_KEY, isPro ? true : false, 1);

    return Promise.resolve({
      fetchedOverNetwork: true,
      data: transformedAuthData,
    });
  } catch (e) {
    throw e;
  }
};

export const fetchSignupBanner = async (params: any) => {
  const requestParams = {
    ...params,
  };
  try {
    const widgets = await getWidgets(requestParams, '');
    return widgets;
  } catch (err) {
    logger.error(err, FETCH_ERROR);
  }
};
